// File: frontend/src/components/ui/MetricCard.js
//
// Shared metric/stat card, extracting the strongest ideas from the Vouchers page
// metric cards: compact title, strong value, supporting text, optional icon,
// optional accent bar, optional progress, subtle hover. Good contrast in both
// themes (surface + text come from the palette; the accent colour is caller-set
// and used only for the icon chip / bar / progress).
//
// Animation (entrance + value fade) is opt-in and automatically disabled when the
// user prefers reduced motion.

import React from "react";
import {
  Box, Paper, Typography, LinearProgress, useTheme, useMediaQuery, alpha,
} from "@mui/material";

export default function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,              // accent colour; defaults to theme primary
  accentBar = true,
  progress,           // 0..100 → renders a progress bar; omit to hide
  onClick,
  sx,
}) {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const accent = color || theme.palette.primary.main;

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: { xs: 2, sm: 2.25 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        cursor: onClick ? "pointer" : "default",
        transition: prefersReducedMotion ? "none" : "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
        "&:hover": onClick || accentBar ? {
          borderColor: alpha(accent, 0.4),
          boxShadow: `0 4px 20px ${alpha(accent, 0.12)}`,
        } : undefined,
        ...sx,
      }}
    >
      {accentBar && (
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, bgcolor: accent }} />
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "text.secondary", letterSpacing: "0.04em" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "clamp(22px, 2.6vw, 30px)", fontWeight: 800, letterSpacing: "-0.02em", mt: 0.5, color: "text.primary", lineHeight: 1.05, wordBreak: "break-word" }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {Icon && (
          <Box sx={{
            width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
            bgcolor: alpha(accent, 0.12), color: accent,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        )}
      </Box>

      {typeof progress === "number" && (
        <Box sx={{ mt: "auto", pt: 1.75 }}>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, progress))}
            sx={{ height: 5, borderRadius: 99, "& .MuiLinearProgress-bar": { borderRadius: 99, bgcolor: accent } }}
          />
        </Box>
      )}
    </Paper>
  );
}
